import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para consultar y monitorear los tickets abiertos repetidos del cliente actual
 */
export const useClientOpenTickets = ({
  idClienteAkasia,
  activeTicketId,
  token,
  iEstadoTicket
}) => {
  const [openTickets, setOpenTickets] = useState([]);
  const [isLoadingOpenTickets, setIsLoadingOpenTickets] = useState(false);
  const lastQueryKeyRef = useRef('');

  const fetchClientOpenTickets = useCallback(async () => {
    if (
      !idClienteAkasia ||
      !activeTicketId ||
      !token ||
      Number(iEstadoTicket) === 2
    ) {
      setOpenTickets([]);
      return [];
    }

    setIsLoadingOpenTickets(true);
    try {
      const response = await fetch(
        `/api/SupportEngine/GetClientOpenTickets?idClienteAkasia=${encodeURIComponent(idClienteAkasia)}&idExcludeTicket=${encodeURIComponent(activeTicketId)}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        const rawList = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : [];

        const normalizedList = rawList.map(item => ({
          idTicket: item.idTicket ?? item.IdTicket,
          vTelefono: item.vTelefono ?? item.VTelefono,
          iEstadoTicket: item.iEstadoTicket ?? item.IEstadoTicket,
          vAsunto: item.vAsunto ?? item.VAsunto,
          idCatNivelSoporte: item.idCatNivelSoporte ?? item.IdCatNivelSoporte,
          idUsuarioAsignado: item.idUsuarioAsignado ?? item.IdUsuarioAsignado,
          vNombreAgenteAsignado: item.vNombreAgenteAsignado ?? item.VNombreAgenteAsignado,
          dCreatedDate: item.dCreatedDate ?? item.DCreatedDate,
          dUltimaActividad: item.dUltimaActividad ?? item.DUltimaActividad
        }));

        setOpenTickets(normalizedList);
        return normalizedList;
      }
    } catch (err) {
      console.error('Error al consultar tickets abiertos del cliente:', err);
    } finally {
      setIsLoadingOpenTickets(false);
    }
    return [];
  }, [idClienteAkasia, activeTicketId, token, iEstadoTicket]);

  useEffect(() => {
    const queryKey = `${idClienteAkasia}_${activeTicketId}_${iEstadoTicket}`;
    if (lastQueryKeyRef.current === queryKey) return;
    lastQueryKeyRef.current = queryKey;

    fetchClientOpenTickets();
  }, [idClienteAkasia, activeTicketId, iEstadoTicket, fetchClientOpenTickets]);

  useEffect(() => {
    const handleTicketActivity = e => {
      const eventClientId = Number(e.detail?.idClienteAkasia);
      const currentClientId = Number(idClienteAkasia);
      if (currentClientId && eventClientId === currentClientId) {
        fetchClientOpenTickets();
      }
    };

    window.addEventListener('akasia:support:ticket_activity', handleTicketActivity);
    return () => {
      window.removeEventListener('akasia:support:ticket_activity', handleTicketActivity);
    };
  }, [idClienteAkasia, fetchClientOpenTickets]);

  return {
    openTickets,
    isLoadingOpenTickets,
    fetchClientOpenTickets
  };
};
